import React from 'react'
import { GroupBase } from 'react-select'
import { AsyncProps } from 'react-select/async'
import AsyncReactSelect from 'react-select/async'

import FormInput from './FormInput'
import useSelectStyles from './useSelectStyles'

export interface Props<
  T,
  IsMulti extends boolean,
  Group extends GroupBase<T> = GroupBase<T>,
> extends AsyncProps<T, IsMulti, Group> {
  /** Label to show on the input. */
  label?: string
  /** HTML for attribute */
  htmlFor: string
  /** Externally defined className for the input wrapper. */
  wrapperClassName?: string
  /** Form field description. */
  description?: string
  /** True if the box is in invalid state. */
  isInvalid?: boolean
  /** True if a value for the field is required */
  isRequired?: boolean
}

/** Implements a wrapper around React Select's AsyncSelect component such that it can
 * be styled using the same styling as the Select component. */
const AsyncSelect = <
  T,
  IsMulti extends boolean,
  Group extends GroupBase<T> = GroupBase<T>,
>({
  htmlFor,
  label,
  description,
  isRequired,
  wrapperClassName,
  options,
  className,
  isInvalid,
  styles,
  ...other
}: Props<T, IsMulti, Group>) => {
  const customStyles = useSelectStyles<T, IsMulti, Group>({
    isInvalid,
  })

  return (
    <FormInput
      label={label}
      description={description}
      htmlFor={htmlFor}
      isRequired={isRequired}
      className={wrapperClassName}
    >
      <AsyncReactSelect<T, IsMulti, Group>
        name={htmlFor}
        options={options}
        className={className}
        styles={{ ...styles, ...customStyles }}
        {...other}
      />
    </FormInput>
  )
}

export default AsyncSelect
